import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../../lib/auth';

interface CreateOption {
  type: 'idea' | 'blog' | 'video' | 'brainstorm';
  icon: string;
  titleKey: string;
  descriptionKey: string;
  color: string;
}

const CREATE_OPTIONS: CreateOption[] = [
  {
    type: 'idea',
    icon: '💡',
    titleKey: 'create.landing.idea.title',
    descriptionKey: 'create.landing.idea.description',
    color: '',
  },
  {
    type: 'blog',
    icon: '📝',
    titleKey: 'create.landing.blog.title',
    descriptionKey: 'create.landing.blog.description',
    color: '',
  },
  {
    type: 'video',
    icon: '🎥',
    titleKey: 'create.landing.video.title',
    descriptionKey: 'create.landing.video.description',
    color: '',
  },
  {
    type: 'brainstorm',
    icon: '✨',
    titleKey: 'create.landing.brainstorm.title',
    descriptionKey: 'create.landing.brainstorm.description',
    color: '',
  },
];

export default function CreateLandingPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuthStore();

  const handleSelectOption = (type: string) => {
    navigate(`?type=${type}`);
  };

  return (
    <div className="max-w-4xl mx-auto p-8">
      {/* Header */}
      <div className="mb-8">
        <button
          onClick={() => navigate('/dashboard')}
          className="inline-flex items-center text-gray-600 dark:text-gray-400 hover:text-black dark:hover:text-white mb-4 transition-colors"
        >
          <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          {t('create.backToDashboard')}
        </button>
        <h1 className="text-4xl font-bold mb-3 dark:text-gray-100">
          {t('create.landing.title')}
        </h1>
        <p className="text-lg text-gray-600 dark:text-gray-400">
          {t('create.landing.subtitle')}
        </p>
      </div>

      {/* Welcome Section */}
      <div className="bg-white dark:bg-gray-800 border-2 border-black dark:border-gray-600 rounded-lg p-6 mb-8">
        <div className="flex items-start gap-4">
          <div className="text-4xl">👋</div>
          <div>
            <h2 className="text-xl font-bold mb-2 dark:text-gray-100">
              {t('create.landing.welcome.title', { name: user?.fullName || 'there' })}
            </h2>
            <p className="text-gray-700 dark:text-gray-300">
              {t('create.landing.welcome.description')}
            </p>
          </div>
        </div>
      </div>

      {/* Create Options Grid */}
      <h2 className="text-2xl font-bold mb-6 dark:text-gray-100">
        {t('create.landing.options.title')}
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        {CREATE_OPTIONS.map((option) => (
          <button
            key={option.type}
            onClick={() => handleSelectOption(option.type)}
            className="bg-white dark:bg-gray-800 border-2 border-black dark:border-gray-600 rounded-lg p-6 text-left hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            {/* Icon */}
            <div className="text-5xl mb-4">{option.icon}</div>

            {/* Title */}
            <h3 className="text-xl font-bold mb-2 dark:text-gray-100">
              {t(option.titleKey)}
            </h3>

            {/* Description */}
            <p className="text-gray-700 dark:text-gray-300 mb-4">
              {t(option.descriptionKey)}
            </p>

            {/* CTA */}
            <div className="flex items-center text-sm font-bold text-black dark:text-white">
              {t('create.landing.startCreating')} →
            </div>
          </button>
        ))}
      </div>

      {/* Quick Tips */}
      <div className="bg-white dark:bg-gray-800 border-l-4 border-blue-500 p-6 rounded-r-lg">
        <h3 className="text-lg font-bold mb-3 dark:text-gray-100 flex items-center gap-2">
          💡 {t('create.landing.tips.title')}
        </h3>
        <ul className="space-y-2 text-gray-700 dark:text-gray-300">
          <li className="flex items-start gap-2">
            <span className="text-blue-500 font-bold">•</span>
            <span>{t('create.landing.tips.tip1')}</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-blue-500 font-bold">•</span>
            <span>{t('create.landing.tips.tip2')}</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-blue-500 font-bold">•</span>
            <span>{t('create.landing.tips.tip3')}</span>
          </li>
        </ul>
      </div>
    </div>
  );
}
